import { useEffect, useRef, useState } from "react";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Check, X, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Inline-editable category chip. Tiny — no design changes elsewhere. */
export default function CategoryEditor({ leadId, value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ""); }, [value]);

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    fetch(`${API}/leads/categories`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setSuggestions((d?.categories || []).map((c) => c.category)))
      .catch(() => {});
  }, [editing]);

  const save = async () => {
    const next = draft.trim().toLowerCase();
    if (!next) {
      toast.error("Category can't be empty (use 'uncategorized' to clear)");
      return;
    }
    if (next === (value || "").toLowerCase()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/leads/${leadId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(`Category set to "${next}"`);
      onChange?.(data.category || next);
      setEditing(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(value || ""); setEditing(false); };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1 group focus:outline-none"
        data-testid="lead-category-edit-btn"
        title="Edit category"
      >
        <Badge
          variant="outline"
          className="border-slate-300 text-slate-600 capitalize group-hover:border-blue-400 group-hover:text-blue-700 transition-colors"
          data-testid="lead-category-chip"
        >
          {(value || "uncategorized").replace(/_/g, " ")}
        </Badge>
        <Pencil className="w-3 h-3 text-slate-300 group-hover:text-blue-500 transition-colors" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 relative" data-testid="lead-category-editor">
      <Input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
        list="lead-category-suggestions"
        placeholder="e.g. restaurant"
        className="h-7 text-sm w-44"
        disabled={saving}
        data-testid="lead-category-input"
      />
      <datalist id="lead-category-suggestions">
        {suggestions.map((s) => <option key={s} value={s} />)}
      </datalist>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="p-1 rounded text-green-600 hover:bg-green-50 disabled:opacity-50"
        title="Save"
        data-testid="lead-category-save-btn"
      >
        <Check className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        className="p-1 rounded text-slate-400 hover:bg-slate-100"
        title="Cancel"
        data-testid="lead-category-cancel-btn"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
